import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowLeft, ShoppingBag, Tag } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useCart } from "../App";
import { motion } from "framer-motion";

const CartPage = () => {
  const { cart, cartLoading, updateCartItem, removeFromCart } = useCart();
  const navigate = useNavigate();

  const formatWeight = (weight) => {
    if (weight >= 1000) return `${weight / 1000}kg`;
    return `${weight}g`;
  };

  const shippingFee = cart.total >= 500 ? 0 : 50;
  const finalTotal = cart.total + shippingFee;

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4">
          <div className="animate-pulse space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-32 bg-card rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="cart-page">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link to="/products" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Continue Shopping
          </Link>

          <h1 className="text-3xl font-bold text-foreground mb-8">Shopping Cart</h1>

          {cart.items.length === 0 ? (
            <div className="text-center py-20">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-6">Add some spicy goodness to your cart!</p>
              <Link to="/products">
                <Button className="rounded-full bg-primary hover:bg-primary/90">
                  Browse Products
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {cart.items.map((item, index) => (
                  <motion.div
                    key={item.product_id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="cart-item bg-card border border-white/5 rounded-xl p-4 flex gap-4"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <img 
                      src={item.image_url} 
                      alt={item.name}
                      className="w-24 h-24 rounded-lg object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="badge-premium text-xs">{formatWeight(item.weight)}</span>
                          <h3 className="text-foreground font-medium mt-1 line-clamp-1">{item.name}</h3>
                          <p className="text-sm text-muted-foreground">₹{item.price} each</p>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.product_id)}
                          className="text-muted-foreground hover:text-red-500 transition-colors p-1"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-3">
                          <button 
                            className="qty-btn"
                            onClick={() => updateCartItem(item.product_id, item.quantity - 1)}
                            data-testid={`decrease-${item.product_id}`}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-8 text-center font-medium text-foreground">{item.quantity}</span>
                          <button 
                            className="qty-btn"
                            onClick={() => updateCartItem(item.product_id, item.quantity + 1)}
                            disabled={item.quantity >= item.stock}
                            data-testid={`increase-${item.product_id}`}
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-lg font-bold text-foreground">₹{item.subtotal}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-card border border-white/5 rounded-xl p-6 sticky top-24" data-testid="order-summary">
                  <h2 className="text-lg font-semibold text-foreground mb-6">Order Summary</h2>
                  
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal ({cart.items.length} items)</span>
                      <span className="text-foreground">₹{cart.total}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Shipping</span>
                      {shippingFee === 0 ? (
                        <span className="text-green-500">FREE</span>
                      ) : (
                        <span className="text-foreground">₹{shippingFee}</span>
                      )}
                    </div>
                    {cart.total < 500 && (
                      <p className="text-xs text-secondary">
                        Add ₹{500 - cart.total} more for free shipping!
                      </p>
                    )}
                    <div className="border-t border-white/5 pt-4">
                      <div className="flex justify-between">
                        <span className="font-semibold text-foreground">Total</span>
                        <span className="text-xl font-bold text-foreground" data-testid="cart-total">₹{finalTotal}</span>
                      </div>
                    </div>
                  </div>

                  {/* Coupon Hint */}
                  <div className="mt-6 p-3 bg-primary/10 rounded-lg flex items-center gap-2">
                    <Tag className="w-4 h-4 text-secondary" />
                    <span className="text-xs text-muted-foreground">Apply coupon at checkout</span>
                  </div>

                  <Button 
                    onClick={() => navigate('/checkout')}
                    className="w-full mt-6 bg-primary hover:bg-primary/90 text-white rounded-full py-6 text-base glow-red"
                    data-testid="checkout-btn"
                  >
                    Proceed to Checkout
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CartPage;
